import {
  isMaterialCharacteristicsWithShade,
  isPartialMaterialCharacteristicsWithShade,
  SOLIDARIZED_MATERIALS,
  type ProductType,
  type DeepPartial,
  type MaterialCharacteristics,
  type ShadeCharacteristics,
} from "@jasper/client-models";
import { Accordion, Box, Button, Stack, Title } from "@mantine/core";

import type { FieldValueProps, ValidationErrors } from "../../types";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { ShadeCharacteristicsForm } from "../shade";
import MaterialTypeSelect from "../material/MaterialTypeSelect";
import { useItemsMaterialCharacteristicsValidation } from "./useItemsMaterialCharacteristicsValidation";

type ItemsMaterialCharacteristics = Record<number, MaterialCharacteristics>;

interface Props extends FieldValueProps<ItemsMaterialCharacteristics> {
  productType: ProductType;
  teeth: number[];
  validateTooth: (
    tooth: number
  ) => ValidationErrors<MaterialCharacteristics> | undefined;
  itemMaterials: { value: string; label: string }[];
  itemShadeTypes: { value: string; label: string }[];
  itemShades: { value: string; label: string }[];
}

export const SolidarizedItemsMaterialCharacteristicsForm = ({
  value,
  errors,
  onBlur,
  onChange,
  productType,
  teeth,
  itemMaterials,

  ...itemCharacteristicsProps
}: Props) => {
  const { t } = useTranslation("forms");

  const itemsCharacteristicsErrors = useMemo(() => {
    if (!errors) {
      return;
    }

    const errorsByTooth = Object.entries(errors).reduce<
      ValidationErrors<ItemsMaterialCharacteristics>
    >((acc, [tooth, materialCharacteristics]) => {
      if (materialCharacteristics?.material) {
        // Shade errors are handled at global level, we only set material errors at tooth level
        return {
          ...acc,
          [tooth]: {
            material: materialCharacteristics.material,
          },
        };
      }

      return acc;
    }, {});

    return errorsByTooth;
  }, [errors]);

  const { sortedTeeth, getToothStatus, validateTooth, resetToothValidation } =
    useItemsMaterialCharacteristicsValidation({
      productType,
      teeth,
      toothValidator: itemCharacteristicsProps.validateTooth,
      globalErrors: itemsCharacteristicsErrors,
      isShadeGlobal: true,
    });

  const [globalShadeCharacteristics, setGlobalShadeCharacteristics] = useState<
    Partial<ShadeCharacteristics> | undefined
  >(
    (() => {
      const found = Object.values(value ?? {}).find(characteristics =>
        isPartialMaterialCharacteristicsWithShade(characteristics ?? {})
      );

      if (found && isPartialMaterialCharacteristicsWithShade(found)) {
        return found.shadeCharacteristics;
      }

      return undefined;
    })()
  );

  const materialTypes = useMemo(
    () =>
      SOLIDARIZED_MATERIALS.find(({ materials }) =>
        materials.some(material =>
          Object.values(value ?? {}).some(
            characteristics => characteristics?.material === material
          )
        )
      )?.type,
    [value]
  );

  // Track closed teeth rather than opened ones, so newly added teeth start opened.
  const [closedItems, setClosedItems] = useState<string[]>([]);

  const openedItems = teeth
    .map(String)
    .filter(tooth => !closedItems.includes(tooth));

  const availableMaterial = useMemo(() => {
    return itemMaterials.filter(material =>
      SOLIDARIZED_MATERIALS.map(({ materials }) => materials)
        .flat()
        .includes(material.value)
    );
  }, [itemMaterials]);

  const selectableMaterials = useMemo(() => {
    if (!materialTypes) {
      return availableMaterial;
    }

    const materialType = SOLIDARIZED_MATERIALS.find(
      material => material.type === materialTypes
    );

    if (!materialType) {
      return availableMaterial;
    }

    return availableMaterial.filter(available =>
      materialType.materials.some(material => available.value === material)
    );
  }, [materialTypes, availableMaterial]);

  const handleSubmit = (next: DeepPartial<ItemsMaterialCharacteristics>) => {
    onChange(next);
  };

  const globalShadeCharacteristicsErrors = useMemo(() => {
    if (!errors) {
      return;
    }

    const shadeCharacteristicsErrors = (() => {
      const found = Object.values(errors).find(
        errorCharacteristics =>
          typeof errorCharacteristics === "object" &&
          "shadeCharacteristics" in (errorCharacteristics ?? {})
      );

      if (found && "shadeCharacteristics" in found) {
        return found.shadeCharacteristics;
      }

      return undefined;
    })() as ValidationErrors<ShadeCharacteristics> | undefined;

    return shadeCharacteristicsErrors;
  }, [errors]);

  return (
    <Stack>
      <ShadeCharacteristicsForm
        value={globalShadeCharacteristics}
        errors={globalShadeCharacteristicsErrors}
        onBlur={() => onBlur?.()}
        onChange={shadeCharacteristics => {
          setGlobalShadeCharacteristics(shadeCharacteristics);
          handleSubmit({
            ...value,
            ...sortedTeeth?.reduce((acc, tooth) => {
              const toothCharacteristics = value?.[tooth] ?? {};

              if (
                isPartialMaterialCharacteristicsWithShade(toothCharacteristics)
              ) {
                return {
                  ...acc,
                  [tooth]: {
                    ...toothCharacteristics,
                    shadeCharacteristics,
                  },
                };
              }

              return acc;
            }, value ?? {}),
          });
        }}
        {...itemCharacteristicsProps}
      />

      <Accordion variant="separated" multiple={true} value={openedItems}>
        {sortedTeeth?.map(tooth => {
          const { toothErrors, icon, title } = getToothStatus(tooth);

          return (
            <Accordion.Item
              key={`tooth-${tooth}`}
              value={tooth.toString()}
              style={toothErrors ? { borderColor: "red" } : undefined}
            >
              <Accordion.Control
                icon={icon}
                onClick={() => {
                  if (openedItems.includes(String(tooth))) {
                    setClosedItems(prevState => [...prevState, String(tooth)]);
                    return;
                  }

                  setClosedItems(prevState =>
                    prevState.filter(item => item !== String(tooth))
                  );
                }}
              >
                <Title c={toothErrors ? "red" : undefined} order={2}>
                  {title}
                </Title>
              </Accordion.Control>
              <Accordion.Panel>
                <Stack gap="lg">
                  <Stack w="100%">
                    <MaterialTypeSelect
                      clearable
                      itemMaterials={selectableMaterials}
                      value={value?.[tooth]?.material}
                      errors={
                        typeof errors?.[tooth] === "string"
                          ? errors?.[tooth]
                          : errors?.[tooth]?.material
                      }
                      onChange={newMaterial => {
                        resetToothValidation(tooth);

                        const materialType = newMaterial
                          ? SOLIDARIZED_MATERIALS.find(material =>
                              material.materials.includes(newMaterial)
                            )?.type
                          : undefined;

                        if (!materialType && newMaterial) {
                          return;
                        }

                        handleSubmit({
                          ...value,
                          [tooth]: {
                            ...value?.[tooth],
                            material: newMaterial,
                            shadeCharacteristics:
                              newMaterial &&
                              isMaterialCharacteristicsWithShade({
                                material: newMaterial,
                              })
                                ? globalShadeCharacteristics
                                : undefined,
                          },
                        });
                      }}
                    />
                  </Stack>
                  <Box>
                    <Button
                      w="auto"
                      onClick={() => {
                        const isValidatedTooth = validateTooth(tooth);

                        if (!isValidatedTooth) {
                          return;
                        }

                        setClosedItems(prevState => [
                          ...prevState,
                          String(tooth),
                        ]);
                      }}
                    >
                      {t("common.validate")}
                    </Button>
                  </Box>
                </Stack>
              </Accordion.Panel>
            </Accordion.Item>
          );
        })}
      </Accordion>
    </Stack>
  );
};
